import { HttpClient, HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { BaseHttpClientService } from './base-http-client.service';

@Injectable()
export class SiteWorkOrderProceduresService extends BaseHttpClientService {

  constructor(
    private http: HttpClient,
  ) {
    super()
  }

  // Create Procedure
  public createWorkOrderProcedure(obj: any): Observable<any> {
    const jsonDataMultipartName = 'workOrderProcedure';
    const fileMultipartName = 'procedureFiles';

    const body = this.buildProcedurePayload({
      name: obj.name,
      description: obj.description,
      revision: obj.revision,
      minimumPartySize: obj.minimumPartySize,
      assetCategoryId: obj.assetCategory,
      competencies: obj.competencies,
      preapproved: obj.preapproved,
      siteId: obj.siteId,
      maintenanceId: obj.maintenanceId,
      checkOnSiteFormId: obj.checkOnSiteFormId,
      checkOffSiteFormId: obj.checkOffSiteFormId
    });

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    obj.documents.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http.post(environment.apiUrl + 'api/ext-work-order-procedures', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('The file is infected and cannot be uploaded!');
        }
        return this.handleError(error);
      }));
  }

  // Add Additional Documents to Procedure
  public addDocumentsToProcedure(obj): Observable<any> {
    const jsonDataMultipartName = 'workOrderProcedure';
    const fileMultipartName = 'procedureFiles';

    const body = this.buildProcedurePayload({
      name: obj.name,
      description: obj.description,
      revision: obj.revision,
      minimumPartySize: obj.minimumPartySize,
      assetCategoryId: obj.assetCategory,
      competencies: obj.competencies,
      manufacturer: obj.manufacturer,
      models: obj.models,
      ratings: obj.ratings,
      preapproved: obj.preapproved,
      siteId: obj.siteId,
      maintenanceId: obj.maintenanceId,
      checkOnSiteFormId: obj.checkOnSiteFormId,
      checkOffSiteFormId: obj.checkOffSiteFormId,
    });

    // multipart/form-data, append each part
    const formData = new FormData();
    formData.append(jsonDataMultipartName, new Blob([JSON.stringify(body)], { type: 'application/json' }));

    obj.documents.forEach((file: File) => {
      formData.append(fileMultipartName, file, file.name);
    });

    return this.http.patch(environment.apiUrl + 'api/ext-work-order-procedures/' + obj.id + '/add-documents', formData)
      .pipe(catchError((error) => {
        console.log('Error', error.error)
        if (error.error && error.error.status === 400 && error.error.errorKey === 'Infected') {
          throw new Error('The file is infected and cannot be uploaded!');
        }
        return this.handleError(error);
      }));
  }

  // Update Procedure
  public updateWorkOrderProcedure(procedure) {
    return this.http.put(
      environment.apiUrl + 'api/ext-work-order-procedures/' + procedure.id,
      this.buildProcedurePayload(procedure)
    )
      .pipe(catchError(this.handleError));
  }

  private buildProcedurePayload(procedure: any = {}): any {
    const models = Array.isArray(procedure.models)
      ? procedure.models
      : (procedure.model ? [procedure.model] : []);
    const ratings = Array.isArray(procedure.ratings)
      ? procedure.ratings
      : (procedure.rating ? [procedure.rating] : []);
    const competencies = this.normalizeCompetencies(procedure.competencies);
    return {
      ...procedure,
      manufacturer: procedure.manufacturer || procedure.oem || '',
      models,
      ratings,
      competencies,
    };
  }

  private normalizeCompetencies(competencies: any[]): any[] {
    if (Array.isArray(competencies) && competencies.length) {
      return competencies.map((item) => ({
        competencyTypeId: item.competencyTypeId,
        applyToParty: !!item.applyToParty,
        manufacturer: item.manufacturer || '',
        models: Array.isArray(item.models) ? item.models : [],
        ratings: Array.isArray(item.ratings) ? item.ratings : [],
      }));
    }

    return [];
  }

  // Delete a Procedure
  public deleteWorkOrderProcedure(workOrderProcedureId) {
    return this.http.delete(environment.apiUrl + 'api/ext-work-order-procedures/' + workOrderProcedureId)
      .pipe(catchError(this.handleError));
  }

  public getWorkOrderProcedureById(workOrderProcedureId) {
    return this.http.get(environment.apiUrl + 'api/ext-work-order-procedures/' + workOrderProcedureId)
      .pipe(catchError(this.handleError));
  }

  public getAllProceduresByAsset(assetId) {
    return this.http.get(environment.apiUrl + 'api/ext-work-order-procedures/by-asset/' + assetId, { observe: 'response' })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  public getOemListBySite(siteId) {
    return this.http.get(environment.apiUrl + 'api/ext-work-order-procedures/oem-list-by-site/' + siteId, { observe: 'response' })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  // Get all work orders procedures by site id
  public getAllWorkOrderProceduresBySite(siteId, pagination, sort, filter) {
    const params = this.createQueryStrings(pagination, sort, filter);
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-work-order-procedures/by-site/' + siteId, { params, observe: 'response', headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }



  // Get all work orders procedures by contractor (maintenanceId)
  public getAllWorkOrderProceduresByContractor(maintenanceId, pagination, sort, filter) {
    const params = this.createQueryStrings(pagination, sort, filter);
    const headers = this.applyLatestOnlyHeader();

    return this.http.get(environment.apiUrl + 'api/ext-work-order-procedures/by-contractor/' + maintenanceId, { params, observe: 'response', headers })
      .pipe(map(this.handleDataWithHeaders))
      .pipe(catchError(this.handleError));
  }

  private handleDataWithHeaders(res: HttpResponse<any>) {
    const headers = res.headers;
    const body = res.body;
    const data = {
      'headers': headers,
      'body': body
    };
    return data;
  }
}
